import { InspectDescriptions } from "@/model/InspectGensha";
import { KatashikiParams } from "@/model/KatashikiParams";
import { BuhinCategory, BuhinDetail } from "@/model/Buhin";
import { WorkAndBuhinDataService } from "@/model/WorkAndBuhinDataService";
import {
  InspectLineWorkAndBuhinData,
  InspectWorkAndBuhinData,
  JizenRecommendWorkAndBuhinData,
  PartSelectionInspectWorkAndBuhinData,
  PartSelectionRecommendWorkAndBuhinData,
  RecommendWorkAndBuhinData,
  WORK_AND_BUHIN_MODE,
  WorkAndBuhinData,
  WorkAndBuhinMode,
  WorkAndBuhinRuntimeData
} from "@/model/WorkAndBuhinData";
import { Work } from "@/model/Work";
import * as WorkBuhinAdapter from "@/components/work-buhin/WorkBuhinAdapter";
import * as WorkBuhinButton from "@/components/work-buhin/WorkBuhinButton";
import * as WorkBuhinConfirm from "@/components/work-buhin/WorkBuhinConfirm";
import * as WorkBuhinDelete from "@/components/work-buhin/WorkBuhinDelete";
import * as WorkBuhinDisplay from "@/components/work-buhin/WorkBuhinDisplay";
import * as WorkBuhinEdit from "@/components/work-buhin/WorkBuhinEdit";
import * as WorkBuhinExtendsImage from "@/components/work-buhin/WorkBuhinExtendsImage";
import * as WorkBuhinInit from "@/components/work-buhin/WorkBuhinInit";
import * as WorkBuhinManualWork from "@/components/work-buhin/WorkBuhinManualWork";
import * as WorkBuhinNisseiren from "@/components/work-buhin/WorkBuhinNisseiren";
import * as WorkBuhinSave from "@/components/work-buhin/WorkBuhinSave";
import * as WorkBuhinSelection from "@/components/work-buhin/WorkBuhinSelection";
import * as WorkBuhinTab from "@/components/work-buhin/WorkBuhinTab";

function createBaseWorkAndBuhinDataService(params: {
  data: WorkAndBuhinData;
  mode: WorkAndBuhinMode;
}): WorkAndBuhinDataService {
  const data = params.data;
  const mode = params.mode;

  return {
    mode,

    hasTypedData(): boolean {
      return mode != WORK_AND_BUHIN_MODE.NONE;
    },

    isInspectData(): boolean {
      return mode == WORK_AND_BUHIN_MODE.INSPECT;
    },

    isJizenRecommendData(): boolean {
      return mode == WORK_AND_BUHIN_MODE.JIZEN_RECOMMEND;
    },

    isInspectLineData(): boolean {
      return mode == WORK_AND_BUHIN_MODE.INSPECT_LINE;
    },

    isRecommendData(): boolean {
      return mode == WORK_AND_BUHIN_MODE.RECOMMEND;
    },

    isPartSelectionInspectData(): boolean {
      return mode == WORK_AND_BUHIN_MODE.PART_SELECTION_INSPECT;
    },

    isPartSelectionRecommendData(): boolean {
      return mode == WORK_AND_BUHIN_MODE.PART_SELECTION_RECOMMEND;
    },

    getTypedData(): WorkAndBuhinData | null {
      return this.hasTypedData() ? data : null;
    },

    getInspectData(): InspectWorkAndBuhinData | null {
      return this.isInspectData() ? data as InspectWorkAndBuhinData : null;
    },

    getJizenRecommendData(): JizenRecommendWorkAndBuhinData | null {
      return this.isJizenRecommendData()
        ? data as JizenRecommendWorkAndBuhinData
        : null;
    },

    getInspectLineData(): InspectLineWorkAndBuhinData | null {
      return this.isInspectLineData() ? data as InspectLineWorkAndBuhinData : null;
    },

    getRecommendData(): RecommendWorkAndBuhinData | null {
      return this.isRecommendData() ? data as RecommendWorkAndBuhinData : null;
    },

    getPartSelectionInspectData(): PartSelectionInspectWorkAndBuhinData | null {
      return this.isPartSelectionInspectData()
        ? data as PartSelectionInspectWorkAndBuhinData
        : null;
    },

    getPartSelectionRecommendData(): PartSelectionRecommendWorkAndBuhinData | null {
      return this.isPartSelectionRecommendData()
        ? data as PartSelectionRecommendWorkAndBuhinData
        : null;
    },

    /*
     * WHY: WorkAndBuhin.vue の既存処理は同じ data へ読み書きするため、
     * mode で固定した service から runtime data を返して移管を段階化する。
     */
    getRuntimeData(): WorkAndBuhinRuntimeData {
      return this.getInspectData() ||
        this.getJizenRecommendData() ||
        this.getInspectLineData() ||
        this.getRecommendData() ||
        this.getPartSelectionInspectData() ||
        this.getPartSelectionRecommendData() ||
        data as WorkAndBuhinRuntimeData;
    },

    /*
     * WHY: WorkAndBuhin.vue が処理用 data を取得する入口を service 名で明示する。
     * getRuntimeData は互換型への変換、getInputData は画面処理から読む用途を表す。
     */
    getInputData(): WorkAndBuhinRuntimeData {
      return this.getRuntimeData();
    },

    getInspectDescriptionData(): InspectDescriptions {
      return this.getRuntimeData() as InspectDescriptions;
    },

    /*
     * WHY: WorkAndBuhin.vue が data の実体差を意識せずに読む入口を作るため。
     */
    getInspectResult(): any {
      return this.getRuntimeData().inspectResult;
    },

    getSknInspectType(): any {
      return this.getRuntimeData().sknInspectType;
    },

    getManualSeibiList(): any[] {
      return this.getRuntimeData().manualSeibiList;
    },

    setManualSeibiList(manualSeibiList: any[] | null): void {
      this.getRuntimeData().manualSeibiList = manualSeibiList;
    },

    getSeibiList(): any[] {
      return this.getRuntimeData().seibiList;
    },

    hasSeibiList(): boolean {
      return !!this.getSeibiList();
    },

    setSeibiList(seibiList: any[] | null): void {
      this.getRuntimeData().seibiList = seibiList;
    },

    getWorkBuhinSelected(): any {
      return this.getRuntimeData().workBuhinSelected;
    },

    setWorkBuhinSelected(workBuhinSelected: any): void {
      this.getRuntimeData().workBuhinSelected = workBuhinSelected;
    },

    getSknInspectDescriptionId(): any {
      return this.getRuntimeData().sknInspectDescriptionId;
    },

    getSknUketsukeInspectId(): any {
      return this.getRuntimeData().sknUketsukeInspectId;
    },

    /*
     * WHY: Vue 側の直接代入を service に寄せ、次段階で mode 別の差異を
     * service 内に閉じ込められるようにする。
     */
    setShowWork(showWork: any): void {
      this.getRuntimeData().showWork = showWork;
    },

    setBuhinSelected(buhinSelected: any): void {
      this.getRuntimeData().buhinSelected = buhinSelected;
    },

    setSeibiBuhinSelected(index: number, buhinSelected: any): void {
      this.getSeibiList()[index].buhinSelected = buhinSelected;
    },

    setManualSeibiBuhinSelected(index: number, buhinSelected: any): void {
      this.getManualSeibiList()[index].buhinSelected = buhinSelected;
    },

    resolveCreatedDisableSaveTarget(): any[] | null {
      return WorkBuhinInit.resolveCreatedDisableSaveTarget(this.getInputData());
    },

    buildCreatedConfirmRestoreAction(isDialog: boolean): any {
      return WorkBuhinInit.resolveCreatedConfirmRestoreAction({
        data: this.getInputData(),
        isDialog
      });
    },

    buildRestoreBuhinConfirmForDialogAction(isDialog: boolean): any {
      return WorkBuhinConfirm.buildRestoreBuhinConfirmForDialogActionState({
        data: this.getInputData(),
        isDialog
      });
    },

    getTargetWorkForBuhinSave(activeWorkIndex: number, isManualWork: boolean): any {
      return WorkBuhinSave.getTargetWorkForBuhinSave({
        data: this.getInputData(),
        activeWorkIndex,
        isManualWork
      });
    },

    resolveWorkBuhinSocketAction(work: any, status: number): any {
      return WorkBuhinSave.resolveWorkBuhinSocketAction({
        data: this.getInputData(),
        work,
        status
      });
    },

    buildBuhinConfirmEmitActionState(actionParams: {
      status: number;
      seibi: any;
      isManualWork: boolean;
      changeList: any[];
    }): any {
      return WorkBuhinSave.buildBuhinConfirmEmitActionState({
        status: actionParams.status,
        data: this.getInputData(),
        seibi: actionParams.seibi,
        isManualWork: actionParams.isManualWork,
        changeList: actionParams.changeList
      });
    },

    getActiveBuhinConfirmKey(actionParams: {
      activeSeibiId: number | null;
      isManualWork: boolean;
      activeWorkIndex: number;
    }): any {
      return WorkBuhinConfirm.getActiveBuhinConfirmKey({
        activeSeibiId: actionParams.activeSeibiId,
        isManualWork: actionParams.isManualWork,
        activeWorkIndex: actionParams.activeWorkIndex,
        data: this.getInputData()
      });
    },

    buildKeepBuhinConfirmMd15ActionState(actionParams: {
      isDialog: boolean;
      data: any;
    }): any {
      return WorkBuhinConfirm.buildKeepBuhinConfirmMd15ActionState({
        isDialog: actionParams.isDialog,
        data: actionParams.data,
        fallbackData: this.getInputData()
      });
    },

    /*
     * WHY: 手入力作業削除後の callback payload に必要な data 取得を service 内に閉じ込め、
     * WorkAndBuhin.vue から getInputData() の直渡しを減らすため。
     */
    buildManualWorkDeleteCallbackBuhinApplyEffectState(
      effectState: WorkBuhinDelete.ManualWorkDeleteApplyEffectState
    ): WorkBuhinDelete.ManualWorkDeleteCallbackBuhinApplyEffectState {
      return WorkBuhinDelete.buildManualWorkDeleteCallbackBuhinApplyEffectState({
        effectState,
        data: this.getInputData()
      });
    },

    /*
     * WHY: 手入力部品/純正部品削除 state 生成に必要な data 取得を service 内に閉じ込め、
     * WorkAndBuhin.vue から getInputData() の直渡しを減らすため。
     */
    buildManualBuhinDeleteState(actionParams: {
      activeWorkIndex: number;
      isManualWork: boolean;
      listManualBuhin: any[];
      deletedBuhin: any;
      isDialog: boolean;
    }): WorkBuhinDelete.ManualBuhinDeleteState {
      return WorkBuhinDelete.buildManualBuhinDeleteState({
        data: this.getInputData(),
        activeWorkIndex: actionParams.activeWorkIndex,
        isManualWork: actionParams.isManualWork,
        listManualBuhin: actionParams.listManualBuhin,
        deletedBuhin: actionParams.deletedBuhin,
        isDialog: actionParams.isDialog
      });
    },

    /*
     * WHY: 手入力部品/純正部品削除後 callback payload に必要な data 取得を service 内に閉じ込め、
     * WorkAndBuhin.vue から getInputData() の直渡しを減らすため。
     */
    buildManualBuhinDeleteAfterApplyEffectState(
      effectState: WorkBuhinDelete.ManualBuhinDeleteApplyEffectState
    ): WorkBuhinDelete.ManualBuhinDeleteAfterApplyEffectState {
      return WorkBuhinDelete.buildManualBuhinDeleteAfterApplyEffectState({
        effectState,
        data: this.getInputData()
      });
    },

    buildDeleteResetWorkActionState(actionParams: {
      activeWorkIndex: number;
      isManualWork: boolean;
    }): any {
      return WorkBuhinDelete.buildDeleteResetWorkActionState({
        data: this.getInputData(),
        activeWorkIndex: actionParams.activeWorkIndex,
        isManualWork: actionParams.isManualWork
      });
    },

    buildWorkBuhinSelectedResetApplyState(actionParams: {
      work: any;
      manualBuhinLength: number;
      selectedBuhinLength: number;
    }): any {
      return WorkBuhinConfirm.buildWorkBuhinSelectedResetApplyState({
        data: this.getInputData(),
        work: actionParams.work,
        manualBuhinLength: actionParams.manualBuhinLength,
        selectedBuhinLength: actionParams.selectedBuhinLength
      });
    },

    getWorkBuhinSourceMode(
      editSeibi: boolean,
      sourceData?: any
    ): WorkBuhinAdapter.WorkBuhinSourceMode {
      const targetData = sourceData || this.getInputData();
      return WorkBuhinAdapter.resolveWorkBuhinSourceMode({
        data: targetData,
        editSeibi
      });
    },

    buildWorkBuhinContext(actionParams: {
      data?: any;
      editSeibi: boolean;
      activeWorkIndex: number;
      isManualWork: boolean;
    }): WorkBuhinAdapter.WorkBuhinInputContext {
      const sourceData = actionParams.data || this.getInputData();
      return WorkBuhinAdapter.normalizeWorkBuhinInput(
        sourceData,
        WorkBuhinAdapter.buildWorkBuhinNormalizeOptions({
          data: sourceData,
          editSeibi: actionParams.editSeibi,
          activeWorkIndex: actionParams.activeWorkIndex,
          isManualWork: actionParams.isManualWork
        })
      );
    },

    buildWorkBuhinContextUpdateState(
      sourceData?: any
    ): WorkBuhinAdapter.WorkBuhinContextUpdateState {
      const targetData = sourceData || this.getInputData();
      return WorkBuhinAdapter.buildWorkBuhinContextUpdateState({
        data: targetData,
        currentData: this.getInputData()
      });
    },

    /*
     * WHY: setBuhinCate の初期選択判定に必要な data 取得を service 内に閉じ込め、
     * WorkAndBuhin.vue から getInputData() の直渡しを減らすため。
     */
    buildSetBuhinCateEffectState(actionParams: {
      context: WorkBuhinAdapter.WorkBuhinInputContext;
      editSeibi: boolean;
    }): WorkBuhinInit.SetBuhinCateEffectState {
      const sourceData = this.getInputData();
      const action = WorkBuhinInit.resolveSetBuhinCateAction({
        data: sourceData,
        context: actionParams.context,
        editSeibi: actionParams.editSeibi
      });
      return WorkBuhinInit.buildSetBuhinCateEffectState({
        action,
        data: sourceData,
        context: actionParams.context
      });
    },

    /*
     * WHY: 部品種別選択時の fallback 判定に必要な data 取得を service 内に閉じ込め、
     * WorkAndBuhin.vue から getInputData() の直渡しを減らすため。
     */
    buildSelectBuhinShubetsuApplyActionState(actionParams: {
    activeWorkIndex?: number;
      hasBuhinData: boolean;
      buhinShubetsuCd: string;
      buhinBunruiList: any;
      buhinCategory: BuhinCategory[];
      activeBuhinCateIndex: number;
      isManualWork: boolean;
      freeOfChargeBuhinList: BuhinDetail[];
    }): WorkBuhinTab.SelectBuhinShubetsuApplyActionState {
      return WorkBuhinTab.buildSelectBuhinShubetsuApplyActionState({
        hasBuhinData: actionParams.hasBuhinData,
        buhinShubetsuCd: actionParams.buhinShubetsuCd,
        buhinBunruiList: actionParams.buhinBunruiList,
        buhinCategory: actionParams.buhinCategory,
        activeBuhinCateIndex: actionParams.activeBuhinCateIndex,
        data: this.isPartSelectionRecommendData() && actionParams.activeWorkIndex != null
          ? this.getSeibiList()[actionParams.activeWorkIndex] || this.getInputData()
          : this.getInputData(),
        isManualWork: actionParams.isManualWork,
        freeOfChargeBuhinList: actionParams.freeOfChargeBuhinList
      });
    },

    buildCurrentWorkEffectState(actionParams: {
      data?: any;
      context: WorkBuhinAdapter.WorkBuhinInputContext;
      activeWorkIndex: number;
      isManualWork: boolean;
      editSeibi: boolean;
      activeSeibiNm: string | null;
    }): WorkBuhinAdapter.WorkBuhinCurrentWorkEffectState {
      const sourceData = actionParams.data || this.getInputData();
      const resolution = WorkBuhinAdapter.resolveCurrentWork({
        data: sourceData,
        context: actionParams.context,
        activeWorkIndex: actionParams.activeWorkIndex,
        isManualWork: actionParams.isManualWork,
        editSeibi: actionParams.editSeibi,
        activeSeibiNm: actionParams.activeSeibiNm
      });
      return WorkBuhinAdapter.buildCurrentWorkEffectState(resolution);
    },

    /*
     * WHY: 初期化時の disabledSave 判定に必要な data 取得を service 内に閉じ込め、
     * WorkAndBuhin.vue から getInputData() の直渡しを減らすため。
     */
    buildSeibiDisableSaveUpdates(actionParams: {
      seibiList: any[];
      isSeibiDetailIdExist: (payload: any) => boolean;
    }): WorkBuhinInit.SeibiDisableSaveUpdate[] {
      return WorkBuhinInit.buildSeibiDisableSaveUpdates({
        data: this.getInputData(),
        seibiList: actionParams.seibiList,
        isSeibiDetailIdExist: actionParams.isSeibiDetailIdExist
      });
    },

    /*
     * WHY: 初期選択済み作業の判定に必要な data 取得を service 内に閉じ込め、
     * WorkAndBuhin.vue から getInputData() の直渡しを減らすため。
     */
    buildInitialSelectSeibiActionState(
      isManualWork: boolean
    ): WorkBuhinInit.InitialSelectSeibiActionState {
      return WorkBuhinInit.buildInitialSelectSeibiActionState({
        isManualWork,
        data: this.getInputData()
      });
    },

    /*
     * WHY: 画像連携ロード判定に必要な data 取得を service 内に閉じ込め、
     * WorkAndBuhin.vue から getInputData() の直渡しを減らすため。
     */
    buildExtendsImageInspectLoadActionState(
      isCanExtendsImage: boolean
    ): WorkBuhinExtendsImage.ExtendsImageInspectLoadActionState {
      return WorkBuhinExtendsImage.buildExtendsImageInspectLoadActionState({
        isCanExtendsImage,
        data: this.getInputData()
      });
    },

    buildSeibiCleanupState(): WorkBuhinSave.SeibiCleanupState {
      return WorkBuhinSave.buildSeibiCleanupState(this.getInputData());
    },

    removeEditMode(): void {
      WorkBuhinEdit.removeEditModeFromData(this.getInputData());
    },

    buildRecommendWorkRowDisplayState(): WorkBuhinDisplay.RecommendWorkRowDisplayState {
      return WorkBuhinDisplay.buildRecommendWorkRowDisplayState(this.getInputData());
    },

    /*
     * WHY: おすすめ部品選択状態の判定に必要な data 取得を service 内に閉じ込め、
     * WorkAndBuhin.vue から getInputData() の直渡しを減らすため。
     */
    buildRecommendBuhinSelectedState(
      isSelected: boolean
    ): WorkBuhinSelection.RecommendBuhinSelectedState {
      return WorkBuhinSelection.buildRecommendBuhinSelectedState(
        this.getInputData(),
        isSelected
      );
    },

    /*
     * WHY: 一括選択時の inspect type 判定に必要な data 取得を service 内に閉じ込め、
     * WorkAndBuhin.vue から getInputData() の直渡しを減らすため。
     */
    buildSelectAllBuhinState(actionParams: {
      work: any;
      buhinContent: any[];
      osusumeBuhinList: BuhinDetail[];
    }): WorkBuhinSelection.SelectAllBuhinState {
      return WorkBuhinSelection.buildSelectAllBuhinState({
        data: this.getInputData(),
        work: actionParams.work,
        buhinContent: actionParams.buhinContent,
        osusumeBuhinList: actionParams.osusumeBuhinList
      });
    },

    /*
     * WHY: 確定ボタン活性判定に必要な data 取得を service 内に閉じ込め、
     * WorkAndBuhin.vue から getInputData() の直渡しを減らすため。
     */
    resolveWorkBuhinButtonDisabledState(actionParams: {
      work?: Work;
      isManualWork: boolean;
      activeWorkIndex: number;
      listSelectedBuhin: BuhinDetail[];
      listManualBuhin: BuhinDetail[];
    }): WorkBuhinButton.WorkBuhinButtonDisabledState {
      return WorkBuhinButton.resolveWorkBuhinButtonDisabledState({
        work: actionParams.work,
        data: this.getInputData(),
        isManualWork: actionParams.isManualWork,
        activeWorkIndex: actionParams.activeWorkIndex,
        listSelectedBuhin: actionParams.listSelectedBuhin,
        listManualBuhin: actionParams.listManualBuhin
      });
    },

    /*
     * WHY: 日整連・純正部品ロード判定に必要な data 取得を service 内に閉じ込め、
     * WorkAndBuhin.vue から getInputData() の直渡しを減らすため。
     */
    shouldLoadNisseirenJunsei(actionParams: {
      isUseNisseiren: number;
      katashikiParams: Partial<KatashikiParams>;
    }): boolean {
      return WorkBuhinNisseiren.shouldLoadNisseirenJunsei({
        data: this.getInputData(),
        isUseNisseiren: actionParams.isUseNisseiren,
        katashikiParams: actionParams.katashikiParams
      });
    },

    /*
     * WHY: 手入力作業の inspect description 生成に必要な data 取得を service 内に閉じ込め、
     * WorkAndBuhin.vue から data 形状の直接参照を減らすため。
     */
    buildManualWorkInspectDescription(work: Work): any {
      return WorkBuhinManualWork.buildManualWorkInspectDescription(
        this.getInputData(),
        work
      );
    }
  };
}

/*
 * WHY: mode 未指定の呼び出し元が残っている間だけの互換 fallback。
 * 有効な mode が渡る呼び出し元は下の mode 別 factory に寄せる。
 */
export function createNoneWorkAndBuhinDataService(
  data: WorkAndBuhinData
): WorkAndBuhinDataService {
  return createBaseWorkAndBuhinDataService({
    data,
    mode: WORK_AND_BUHIN_MODE.NONE
  });
}

export function createInspectWorkAndBuhinDataService(
  data: InspectWorkAndBuhinData
): WorkAndBuhinDataService {
  return createBaseWorkAndBuhinDataService({
    data,
    mode: WORK_AND_BUHIN_MODE.INSPECT
  });
}

export function createJizenRecommendWorkAndBuhinDataService(
  data: JizenRecommendWorkAndBuhinData
): WorkAndBuhinDataService {
  return createBaseWorkAndBuhinDataService({
    data,
    mode: WORK_AND_BUHIN_MODE.JIZEN_RECOMMEND
  });
}

export function createInspectLineWorkAndBuhinDataService(
  data: InspectLineWorkAndBuhinData
): WorkAndBuhinDataService {
  return createBaseWorkAndBuhinDataService({
    data,
    mode: WORK_AND_BUHIN_MODE.INSPECT_LINE
  });
}

export function createRecommendWorkAndBuhinDataService(
  data: RecommendWorkAndBuhinData
): WorkAndBuhinDataService {
  return createBaseWorkAndBuhinDataService({
    data,
    mode: WORK_AND_BUHIN_MODE.RECOMMEND
  });
}

export function createPartSelectionInspectWorkAndBuhinDataService(
  data: PartSelectionInspectWorkAndBuhinData
): WorkAndBuhinDataService {
  return createBaseWorkAndBuhinDataService({
    data,
    mode: WORK_AND_BUHIN_MODE.PART_SELECTION_INSPECT
  });
}

export function createPartSelectionRecommendWorkAndBuhinDataService(
  data: PartSelectionRecommendWorkAndBuhinData
): WorkAndBuhinDataService {
  return createBaseWorkAndBuhinDataService({
    data,
    mode: WORK_AND_BUHIN_MODE.PART_SELECTION_RECOMMEND
  });
}
